// client/src/components/secure/ZoningSearchTab.js — Property Search → Zoning search.
//
// Owner, 22 Oct 2026: the zoning portal search for all properties and all areas, with a
// selectable business, for every agent. Pick a business (a Zoning Library use), the towns
// (all by default) and the verdicts wanted; the server (routes/propertyZoningSearch.js)
// answers with each town's library verdict and every property the system knows — site
// search finds from every search plus our own listings — one row per building, zoned for
// that business. Only public listing facts are shown: nothing about any client's search.
// "Check an address" zones any address for the same business (ZoningLookupBox).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { Card, ErrorLine, Btn, Empty } from './territory/Shared';
import DataList from '../shared/DataList';
import ZoningLookupBox, { LABEL_TONE } from './ZoningLookupBox';
import { BusinessPicker, ResearchNowButton } from './BusinessPicker';
import LookupLinks, { AddressLink, LinkNote } from '../shared/LookupLinks';
import { classifyListingUrl, SOURCE_NAME } from '../../utils/listingLinks';

const API = '/api/property-search/zoning-search';
const qs = (o) => new URLSearchParams(Object.entries(o).filter(([, v]) => v !== '' && v !== null && v !== undefined)).toString();
const VERDICT_SHORT = { by_right: 'BY RIGHT', special_use: 'HEARING', not_allowed: 'NOT ALLOWED', unverified: 'UNKNOWN' };
const label = 'block text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1';

const Badge = ({ v }) => (
    <span className={`inline-block whitespace-nowrap rounded border px-2 py-0.5 text-[10px] font-semibold ${LABEL_TONE[v] || LABEL_TONE.unverified}`}>
        {VERDICT_SHORT[v] || 'UNKNOWN'}
    </span>
);
const ext = (url, text) => (url ? <a href={url} target="_blank" rel="noreferrer" className="text-indigo-700 hover:underline">{text} ↗</a> : null);

const TownPicker = ({ towns, value, onChange }) => {
    const [q, setQ] = useState('');
    const shown = towns.filter((t) => t.town.toLowerCase().includes(q.trim().toLowerCase()));
    const all = value.length === 0;
    const toggle = (t) => onChange(value.includes(t) ? value.filter((x) => x !== t) : [...value, t]);
    return (
        <div>
            <span className={label}>Areas</span>
            <label className="flex items-center gap-2 text-sm mb-1">
                <input type="checkbox" checked={all} onChange={() => onChange(all ? (shown[0] ? [shown[0].town] : []) : [])} />
                All towns ({towns.length})
            </label>
            {!all && (
                <div className="rounded border border-slate-200 p-2">
                    <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a town…" aria-label="Find a town"
                        className="mb-2 w-full rounded border border-slate-300 p-1.5 text-sm" />
                    <div className="max-h-48 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-x-3">
                        {shown.map((t) => (
                            <label key={t.town} className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={value.includes(t.town)} onChange={() => toggle(t.town)} />
                                {t.town}{!t.in_library && <span className="text-[10px] text-slate-400">not in library</span>}
                            </label>
                        ))}
                        {!shown.length && <span className="text-xs text-slate-500">No town matches.</span>}
                    </div>
                    <p className="mt-1 text-xs text-slate-500">{value.length} chosen · <button type="button" className="underline" onClick={() => onChange([])}>all towns</button></p>
                </div>
            )}
        </div>
    );
};

const ZoningSearchTab = () => {
    const api = useApiClient();
    const [opts, setOpts] = useState(null);
    const [state, setState] = useState('IL');
    const [use, setUse] = useState('');
    const [towns, setTowns] = useState([]);
    const [verdicts, setVerdicts] = useState(['by_right']);
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        api.get(`${API}/options?${qs({ state })}`).then((o) => {
            setOpts(o);
            setUse((u) => (u && o.uses.includes(u) ? u : (o.default_use || '')));
            setTowns((ts) => ts.filter((t) => o.towns.some((x) => x.town === t)));
        }).catch((e) => setError(e.message || 'Could not load the choices.'));
    }, [api, state]);

    const run = useCallback(async () => {
        if (!use) return;
        setBusy(true); setError('');
        try {
            setResult(await api.get(`${API}?${qs({ use, state, towns: towns.join(','), verdicts: verdicts.join(',') })}`));
        } catch (e) { setError(e.message || 'The search did not work.'); } finally { setBusy(false); }
    }, [api, use, state, towns, verdicts]);

    // First answer as soon as the business is known.
    const [ranOnce, setRanOnce] = useState(false);
    useEffect(() => { if (use && !ranOnce) { setRanOnce(true); run(); } }, [use, ranOnce, run]);

    const toggleVerdict = (v) => setVerdicts((xs) => (xs.includes(v) ? xs.filter((x) => x !== v) : [...xs, v]));

    const townCols = useMemo(() => [
        { key: 'town', label: 'Town' },
        { key: 'verdict_status', label: 'Library verdict', value: (r) => VERDICT_SHORT[r.verdict_status], render: (r) => (
            <div><Badge v={r.verdict_status} />{r.via && <span className="block text-[10px] text-slate-500">via {r.via}</span>}
                <span className="block text-xs text-slate-600 max-w-xs">{r.verdict}</span></div>) },
        { key: 'districts_by_right', label: 'By-right districts' },
        { key: 'districts_hearing', label: 'Hearing districts' },
        { key: 'citation', label: 'Code', render: (r) => <span>{r.citation || ''} {ext(r.source_url, 'source')}</span>, value: (r) => [r.citation, r.source_url].filter(Boolean).join(' ') },
        { key: 'verified_on', label: 'Verified', type: 'date', render: (r) => (r.verified_on ? <span>{new Date(`${r.verified_on}T12:00:00`).toLocaleDateString()}{!r.library_verified && <span className="block text-[10px] text-amber-700">AI draft</span>}</span> : <span className="text-slate-400">not verified</span>) },
        { key: 'properties', label: 'Properties', type: 'number' },
    ], []);

    const propCols = useMemo(() => [
        { key: 'town', label: 'Town', sortValue: (r) => r.sort_key },
        { key: 'address', label: 'Address', sortValue: (r) => r.sort_key, render: (r) => (
            <div>
                <span>{r.our_listing ? r.address : <AddressLink c={r} className="text-indigo-700 hover:underline" />}
                    {r.our_listing && <span className="ml-1 rounded bg-indigo-100 px-1.5 text-[10px] font-semibold text-indigo-800">OUR LISTING</span>}</span>
                {!r.our_listing && <LinkNote c={r} />}
                <LookupLinks c={r} uses={use} />
            </div>) },
        { key: 'district', label: 'District', render: (r) => (r.district ? (
            <span title={r.district_from || ''}>{r.district}
                <span className={`block text-[10px] ${r.district_confirmed ? 'text-slate-500' : 'font-semibold text-amber-700'}`}>{r.district_confirmed ? 'confirmed' : 'unconfirmed'}</span>
                {r.district_conflict && <span className="block text-[10px] font-semibold text-amber-700">sources disagree</span>}
            </span>) : <span className="text-slate-400">not known</span>), value: (r) => [r.district, r.district ? (r.district_confirmed ? 'confirmed' : 'unconfirmed') : ''].filter(Boolean).join(' ') },
        { key: 'verdict', label: 'Verdict', value: (r) => VERDICT_SHORT[r.verdict], render: (r) => <span title={r.verdict_reason}><Badge v={r.verdict} /></span> },
        { key: 'size', label: 'Size' },
        { key: 'rate', label: 'Rate' },
        { key: 'listing_agent', label: 'Listing agent' },
        { key: 'listing_brokerage', label: 'Brokerage' },
        { key: 'agent_phone', label: 'Phone', render: (r) => (r.agent_phone ? <a className="text-indigo-700" href={`tel:${r.agent_phone}`}>{r.agent_phone}</a> : '') },
        { key: 'source', label: 'Source', value: (r) => [r.source, r.source_url].filter(Boolean).join(' '), render: (r) => {
            if (!r.source_url) return r.source || '';
            const k = classifyListingUrl(r.source_url).kind;
            return ext(r.source_url, k === 'building' ? `${SOURCE_NAME[r.source] || r.source || 'building'} building page` : k === 'search' ? `${SOURCE_NAME[r.source] || r.source || ''} search page` : (SOURCE_NAME[r.source] || r.source || 'listing'));
        } },
        { key: 'last_seen', label: 'Last seen', type: 'date' },
    ], [use]);

    if (!opts && !error) return <Empty>Loading…</Empty>;
    return (
        <div className="space-y-4">
            <Card title="Zoning search" right={result && <Btn kind="secondary" onClick={() => window.print()}>Print</Btn>}>
                <div className="space-y-3 print:hidden">
                    <p className="text-sm text-slate-600">Pick a business and the areas: you get each town's Zoning Library answer and every property the office knows there — site-search finds and our own listings, one row per building — with the verdict for that business.</p>
                    <div className="grid gap-3 md:grid-cols-3">
                        <div>
                            <label className={label} htmlFor="zs-use">Business</label>
                            <BusinessPicker id="zs-use" value={use} onChange={setUse} />
                            <p className="mt-1 text-xs text-slate-500">Any business in the Business Library — type any word. Towns not researched for it yet can be sent for research.</p>
                        </div>
                        <div>
                            <label className={label} htmlFor="zs-state">State</label>
                            <select id="zs-state" value={state} onChange={(e) => setState(e.target.value)} className="w-full rounded border border-slate-300 p-2 text-sm bg-white">
                                {(opts ? opts.states : ['IL']).map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                        <div>
                            <span className={label}>Verdict</span>
                            {(opts ? opts.verdicts : []).map((v) => (
                                <label key={v.value} className="flex items-center gap-2 text-sm">
                                    <input type="checkbox" checked={verdicts.includes(v.value)} onChange={() => toggleVerdict(v.value)} /> {v.label}
                                </label>
                            ))}
                        </div>
                    </div>
                    <TownPicker towns={opts ? opts.towns : []} value={towns} onChange={setTowns} />
                    <div className="flex flex-wrap items-center gap-2">
                        <Btn onClick={run} disabled={busy || !use || !verdicts.length}>{busy ? 'Searching…' : 'Search'}</Btn>
                        {!verdicts.length && <span className="text-xs text-amber-700">Tick at least one verdict.</span>}
                    </div>
                    <ErrorLine error={error} />
                </div>
                {result && (
                    <p className="mt-3 text-sm text-slate-700">
                        <strong>{result.use}</strong> · {result.towns.length ? `${result.towns.length} town${result.towns.length === 1 ? '' : 's'}` : 'all towns'} · {result.verdicts.map((v) => VERDICT_SHORT[v]).join(', ')}
                        {' · '}showing {result.shown} of {result.total} propert{result.total === 1 ? 'y' : 'ies'}{result.capped ? ' (narrow the towns to see the rest)' : ''}
                    </p>
                )}
            </Card>

            {result && (result.not_researched || []).length > 0 && (
                <Card title={`Not researched for ${result.use} (${result.not_researched.length} town${result.not_researched.length === 1 ? '' : 's'})`} className="print:hidden">
                    <p className="text-sm text-slate-600">{result.not_researched.join(', ')}</p>
                    <div className="mt-2"><ResearchNowButton key={`${result.use}-${result.not_researched.join('|')}`} business={result.use} towns={result.not_researched} origin="zoning_search" small={false} /></div>
                    <p className="mt-1 text-xs text-slate-500">The office computer researches each town's code; answers arrive in the Zoning Library as "AI draft — confirm".</p>
                </Card>
            )}

            {result && (
                <Card title={`Towns (${result.summary.length})`}>
                    <DataList rows={result.summary} rowKey="town" columns={townCols} csvName="zoning-search-towns" defaultSort={{ key: 'town', dir: 'asc' }}
                        empty="No town in the chosen areas gives that verdict for this business." />
                </Card>
            )}

            {result && (
                <Card title={`Properties (${result.shown}${result.capped ? ` of ${result.total}` : ''})`}>
                    <DataList rows={result.properties} rowKey="key" columns={propCols} csvName="zoning-search-properties" defaultSort={{ key: 'town', dir: 'asc' }}
                        filters={[{ key: 'town', label: 'Town' }, { key: 'verdict', label: 'Verdict' }]}
                        note={`Site-search finds seen in the last ${result.stale_days} days and our active listings. Hover a verdict for the reason. ${result.capped ? `Only the first ${result.shown} of ${result.total} are loaded.` : ''}`}
                        empty="No known property matches. Try more verdicts or more towns." />
                </Card>
            )}

            {use && (
                <Card title="Check an address" className="print:hidden">
                    <ZoningLookupBox key={use} useCategory={use} />
                </Card>
            )}
        </div>
    );
};

export default ZoningSearchTab;
